/**
 * Update 2023-Oct-02
 * objectsAreEqual() use deep compare
 *
 * Update 2023-Aug-25
 * 1. Use useStaticMemo to replace useMemo, which may clear the cache in future ReactJs.
 *
 * Update 2023-Aug-24
 * 1. Added ReactModel.sync_undef_sub_model
 * 2. Allow undefined root object
 *
 * Update 2023-July-22
 * 1. getModel_mapFields() renamed to getModel_remap(), supports max 5 variables; optional fields supported
 * 2. added getModel_pick(), works like the type Pick<T,K>
 * 3. removed "+proxy", which is replaced by getModel_mapFields(null, proxy);
 *
 *
 * Update 2023-July-19
 * 1. Sub-model can be created for optional member objects
 * 2. Rename useModel().sub_model to useModel().model
 * 3. HInput/HSelect/HHelp/HTextArea... can use the nullable field
 *
 * Update 2023-May-15
 * 1. Can defined controller in constructor
 * 2. Renamed 'ctrl' to 'c'
 *
 * Update 2023-April-15
 * 1. Add helper function: useComputed()
 * 2. Can call addCtrl(xxx) to add a controller function, and use ctrl.xxx() to run the controller.
 * See end of the test file for usage examples.
 *
 * Update 2023-Mar-22
 * getModel(...) can update the getter and setter methods. This means a sub model can be updated.
 * Pass { allow_updated: true } to enable this feature.
 *
 * Update 2022-DEC-18
 * validator can be async.
 *
 * Update 2022-NOV-27
 * useModel({getter:..., setter:...}) does not need 'key'.
 * Because: the same component may be rendered twice and one of them may be destroyed by Reactjs and cause problems
 *
 */
import { useEffect, useState } from "react";
import { type validateOneField } from "df-validator";
import { freezeObject } from "df-helpers/dist/object/freeze-object";
import { objectsAreEqual } from "df-helpers/dist/object/objects-are-equal";
import { thawMember } from "df-helpers/dist/object/thaw-member";
import { thawObject } from "df-helpers/dist/object/thaw-object";
import { BusyWorker } from "df-helpers/dist/busy-worker";
import { _useId } from "../react-model-ui/hooks/use-id";
import { getMemberValueSafe } from "df-helpers/dist/object/get-member-value-safe";

type KS<T> = Extract<keyof T, string>;

let model_id = 0;
let g_update_counter = 0;

type TObservers<T> = {
	[observer_name: string | number | symbol]: {
		onModelChange: (value: T) => void;
		observer_name: string;
	};
};

//------------------------------------------------------------------------------
// ReactModel_Internal - Object observer
//------------------------------------------------------------------------------

type TReactModelOptions<T, TCtrl={}> = {
	model: T;
	/** Key is for debug and display purposely. If not provided, a sequential number will be provided. */
	key?: string;
	/** default freeze the object recursively. if an object is "sealed", then it will not be frozen */
	no_recursive_freeze?: boolean;
	/**
	 * * Use with care.
	 * * What it does: no freeze at all, and there is only one instance of the data model.
	 * * Why is it needed: more efficient for large data
	 * * How to use
	 * 	* Call "notifyObservers()" to refresh the UI
	 * 	* Calling setState() is not suggested: directly modify the object instead
	 * 	* Setting sub state is not suggested: directly modify the object instead
	 */
	no_freeze?: boolean;
	/**
	 * Controller is a collection of functions that changes the state of the model.
	 * * You can also call addCtrl() to create controller functions.
	 * * Sample code:
	 * ```ts
     * const model_test = new ReactModel(
     * {
     *   model:{a:1,b:2},
     *   controller: {
     *     load:(value:string)=>{
     *       	return true;
     *     },
     *     save:(value:string)=>{
     *       	return true;
     *     }
     *   }
     * });
	 * ```
	 */
	controller?: TCtrl;
}

class ReactModel_Internal<T> {
	private _key: string;
	protected _model: T;
	public model_id = ++model_id; //for debug purpose
	protected _observers: TObservers<T> | null = null;
	protected _recursive_freeze: boolean;
	protected _no_freeze: boolean;

	get no_freeze() {
		return this._no_freeze;
	}

	constructor({
		model,
		key,
		no_recursive_freeze,
		no_freeze,
	}: TReactModelOptions<T>) {
		this._key = key || "M" + this.model_id;
		this._recursive_freeze = !no_recursive_freeze;
		this._no_freeze = !!no_freeze;
		if (no_freeze) this._model = model;
		else this._model = freezeObject(model, this._recursive_freeze, true);
	}

	get key() {
		return this._key;
	}

	public setState(state: T): T {
		if (this._no_freeze) {
			this._model = state;
			this.notifyObservers();
			return this._model;
		} else {
			if (objectsAreEqual(this._model, state, true)) {//deep compare
				if((window as any).__show_dbg_react_model){
					console.log(`[${this.model_id}:${this.key}]: setState: Objects are equal`,this._model, state);
				}
				return this._model;
			}
			this._model = freezeObject(state, this._recursive_freeze, true);
			this.notifyObservers();
			return this._model;
		}
	}

	public notifyObservers() {
		if (this._observers) {
			for (const observer_name in this._observers) {
				const observer = this._observers[observer_name];
				observer.onModelChange(this._model);
				// console.log(this._key + ': Update observer: ' + observer_name + ', value: ' + this._model);
			}
		}
	}

	public setProperty(value: Partial<T>) {
		const v = Object.assign({}, this._model, value);
		return this.setState(v);
	}

	/** 'none' - No thaw. Default.
	 * 'thaw' - Thaw the first level of object
	 * 'deep_thaw' - Deep thaw the object
	 */
	public getState(thaw_object?: "none" | "thaw" | "deep_thaw"): T {
		const m = this._model;
		if (thaw_object) {
			if (m && typeof m === "object") {
				return thawObject(m, thaw_object === "deep_thaw");
			}
		}
		return this._model;
	}

	/**
	 * @description Add a observer function to the model. The function will be called when the model changes.
	 *
	 * @param observer_name Name of the observer. Previous observer of the same name will be replaced.
	 * @param func_state_changed function to be called when the model changes
	 * @param debug_viewer_name A string for debugger
	 * @param first_call if true, call the function immediately.
	 */
	public addObserver(
		observer_name: string | number | symbol,
		func_state_changed: (state: T) => any,
		debug_viewer_name?: string,
		first_call?: boolean,
	) {
		if (!this._observers) this._observers = {};
		this._observers[observer_name] = {
			observer_name: `${observer_name.toString()}${
				debug_viewer_name ? "(" + debug_viewer_name + ") " : ""
			}`,
			onModelChange: func_state_changed,
		};
		if (first_call) {
			func_state_changed(this._model);
		}
		//console.log(`${this.model_id}: ${this.key}: after add Observer: ${this.observers().join(',')}`);
	}

	/** @description Remove an observer by name */
	public removeObserver(observer_name: string | number | symbol) {
		if (!this._observers) return;
		delete this._observers[observer_name];
		if (Object.keys(this._observers).length <= 0) this._observers = null;
		//console.log(`${this.model_id}: ${this.key}: after delete Observer: ${this.observers().join(',')}`);
	}

	/** @description Get a list of observers */
	public observers() {
		const ret = Object.values(this._observers || {}).map(x => x.observer_name);
		return ret;
	}
}

//---------------- react model partial ------------

type TSubModel<T, K> = {
	/**
	 * * "Key" uniquely identifies the sub model of the current model.
	 * * Existing model will be returned
	 * * Otherwise a new model will be created
	 */
	key: string | number | symbol;
	getter: (state: T) => K; //
	setter?: (state: T, new_partial_state: K) => T | void;
	/**
	 * Update the getter/setter functions.
	 *
	 * The sub model is cached under the name of the "key". If "allow_update" is true, then
	 * 1. update getter/setter
	 * 2. if the result of getter changes, call all observers. But the parent model will not be notified
	 */
	allow_update?:boolean;
};

// type TSubModel_OptionalKey<T, K> = Partial<Pick<TSubModel<T, K>,'key'>> & Pick<TSubModel<T, K>,'getter' | 'setter'>
type TSubModel_NoKey<T, K> = Pick<TSubModel<T, K>,'getter' | 'setter' | 'allow_update'>


//------------------------------------------------------------------------------
// ReactModel_Partial - create a partial model, also called "sub model"
//------------------------------------------------------------------------------

class ReactModel_Partial<T> extends ReactModel_Internal<T> {
	protected _partial_models: { [name: string | number | symbol]: ReactModel<any> } = {};
	// private _partial_model_count = 0;

	protected _setter: any;
	protected _getter: any;
	protected _parent_model?: ReactModel<any>;

	/** Life circle is the same as the main model.
	 * - useModel() and getState() observes the partial model
	 * - setState() updates the partial model
	 *
	 * key can be empty if it is known that createPartialModel() is called only once
	 * */
	protected createPartialModel<K>({ getter, setter, key }: TSubModel<T, K>): ReactModel<K> {
		//auto inc key will cause the "addObserver()" not deleted key = '_cr_partial_' + ++this._partial_model_count;
		if (key === undefined) {
			console.error("(CPM214)key missing");
		}

		//error if exists
		if (this._partial_models[key]) {
			console.error("Partial model exists: " + key.toString());
		}

		const partial_model = new ReactModel<K>({
			key: this.key + "." + key.toString(),
			model: getter(this.getState()),
			no_recursive_freeze: !this._recursive_freeze,
			no_freeze: this._no_freeze,
		});

		//add to cache
		partial_model._key_in_parent = key;
		this._partial_models[key] = partial_model;
		//save the value for useModel()
		{
			partial_model._setter = setter;
			partial_model._getter = getter;
			partial_model._parent_model = this as any;
		}
		//partial_model observes the main model. When then main_model changes, call the observers with partial_value
		// this.linkEvents<K>(partial_model, key, getter);

		partial_model.observeParent(this);

		return partial_model;
	}

	private _key_in_parent: number | string | symbol = "";
	private _is_obj = undefined;

	private observeParent<T>(parent_model: ReactModel_Partial<any>) {
		// let last_value = partial_model.getState(); //check changes
		const func_on_change = (full_state: T) => {
			// if (!this._observers) {  this logic is wrong. It will cause the this._model not to update,
			// 	console.log(this.key + ': no observer found');
			// 	return;
			// }
			//get the partial_value
			const new_value = this._getter(full_state);
			//check if the current model is an object
			if (this._is_obj === undefined) {
				this._is_obj = new_value && typeof new_value === "object";
			}
			//if the partial_value changes. Skip undefined, because this happens when an item is deleted, and the rendering will fail
			if (
				(this._no_freeze || new_value !== this.getState()) &&
				(ReactModel.sync_undef_sub_model || (new_value !== undefined || this._is_obj === false))
			) {
				// last_value = new_value;
				// partial_model.setState(new_value);
				// this._model = new_value;
				// // //call observers
				// this.notifyObservers();
				super.setState(new_value);

				// if (this._observers) {
				// 	for (let observer_name in this._observers) {
				// 		const observer = this._observers[observer_name];
				// 		observer.onModelChange(new_value);
				// 	}
				// }
				// //remove if no more observers, remove from main model
				// else {
				// 	// 	parent_model.removeObserver(this.key);
				// 	console.log(this.key + ': no observer found');
				// }
				// console.log(`CHANGED: ${this.model_id} : ${this.key}`);
			} else {
				// console.log(`NOT changed: ${this.model_id} : ${this.key}`);
			}
		};
		parent_model.addObserver(this.key, func_on_change, "id" + this.model_id.toString());

		//call it for change. Just in case the model has changed but not updated
		func_on_change(parent_model.getState());
	}

	private removeChild(partial_model: ReactModel_Partial<any>) {
		if (partial_model._key_in_parent in this._partial_models) {
			delete this._partial_models[partial_model._key_in_parent];
			// this._partial_model_count--;
		}
	}
	public destroy() {
		this._parent_model?.removeChild(this);
		this._parent_model?.removeObserver(this.key);
	}

	/**
	 * If the call back function is used, then the state is thawed in the first level by default. "deep_thaw" will thaw the state recursively
	 */
	public setState(state: ((old_unfrozen_state: T) => T | void) | T, deep_thaw?: "deep_thaw"): T {
		if (typeof state === "function") {
			const cloned_state = thawObject(this.getState(), !!deep_thaw);
			const picked_value = pickValue((state as any)(cloned_state), cloned_state);
			return this.setState(picked_value);
		}
		//when the partial_model changes, save to parent model
		if (this._parent_model && this._setter) {
			// const old_set_state = ret.setState;
			const new_partial_state = state;
			//save to main_model, which will call main_model observers
			const cloned_state = this._parent_model.getState("thaw");
			const picked_value = pickValue(
				this._setter(cloned_state, new_partial_state),
				cloned_state,
			);
			//Notify parent, never notify myself. Because the child model may be an array element, which is to be deleted
			this._parent_model?.setState(picked_value);
			//do not setState() of myself, I will be notified by my parent model
			//xx super.setState(state);
			return new_partial_state;
		} else if (this._parent_model && !this._setter) {
			console.error(
				"Cannot call setState() of the partial model, because the setter is not defined",
			);
			return this._model;
		} else {
			// if (this._parent_model && !this._setter) {
			// 	console.log('Note: not setter defined.');
			// }
			return super.setState(state);
		}
	}

	protected getPartialModel(partial_model_key: string | number | symbol) {
		return this._partial_models[partial_model_key];
	}
}

function pickValue<T>(value: T | undefined | void, default_value: T): T {
	return value === undefined ? default_value : value;
}

type TUseReturn<T> = {
	state: T; //
	setState: (
		state:
			| T //
			| ((old_unfrozen_state: T) => T | void),
	) => T;
	model: ReactModel<T>
};

//------------------------------------------------------------------------------
// ReactModel - add react function "useModel()"
//------------------------------------------------------------------------------

export class ReactModel_React<T> extends ReactModel_Partial<T> {
	private _render_count = 0;
	get renderCount() {
		return this._render_count;
	}

	//-----------------------------------
	//---------- get sub model ----------
	//-----------------------------------
	private _getSubModel<K>(arg: TSubModel<T, K>) {
		let ret = this.getPartialModel(arg.key);
		if (!ret) {
			ret = this.createPartialModel(arg);
		}else if(arg.allow_update){
			const changed = ret._getter !== arg.getter;
			ret._getter = arg.getter;
			ret._setter = arg.setter;
			if(changed){
				//here we don't use ret.setState() because it will notify the parent, and will fail if setter is not defined.
				const value = arg.getter(this._model);
				//here we replace !== with deep compare, to avoid recursive call
				// if(value !== ret._model){
				if(!objectsAreEqual(value, ret._model, true)){
					ret._model = value;
					ret.notifyObservers();
				}
			}
		}
		return ret;
	}
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	private _getSubModel_AddValueField<K>(extra_key: string) {
		return this._getSubModel({
			key: "add-value-member-" + extra_key,
			getter: state => ({ proxy: state }),
			setter: (state, value) => {
				return value.proxy;
			},
		});
	}

	private _getSubModelOfField<K extends keyof T>(
		field_name: K,
		f2: any,
		f3: any,
		f4: any,
		extra_key: string,
	) {
		//if no transformation needed, then return itself.
		if(field_name === undefined) return this;

		let key = (field_name as string) + extra_key;
		if (f2 !== undefined) key += "." + f2;
		if (f3 !== undefined) key += "." + f3;
		if (f4 !== undefined) key += "." + f4;

		return this._getSubModel({
			key: key,
			getter: state => getMemberValueSafe(state, field_name, f2, f3, f4),
			setter: (state, new_val) => {
				if(!state){
					state = {} as any;
				}
				thawMember(state, field_name, f2, f3, f4, { value: new_val });
				return state;
			},
		});
	}

	/**
	 * For models that have no field, this will create a new model, that has a field 'proxy',
	 * so that HInput can connect to it
	 */
	// public getModel(field: "+proxy"): ReactModel<{ proxy: T }>;
	/**
	 * * Returns a model re-used by 'key', using 'getter' and 'setter'
	 * * Call "destroy()" function when it is no longer needed
	 */
	public getModel<K>(arg: TSubModel<T, K>): ReactModel<K>;
	/**
	 * * Returns a model of its member fields
	 * * Call "destroy()" function when it is no longer needed
	 */
	public getModel<
		A extends keyof NonNullable<T>,
		B extends keyof Required<NonNullable<T>>[A],
		C extends keyof Required<Required<NonNullable<T>>[A]>[B],
		D extends keyof Required<Required<Required<NonNullable<T>>[A]>[B]>[C],
	>(field: A, f2: B, f3: C, f4: D): ReactModel<Required<Required<Required<NonNullable<T>>[A]>[B]>[C][D]>;
	/**
	 * * Returns a model of its member fields
	 * * Call "destroy()" function when it is no longer needed
	 */
	public getModel<
		A extends keyof NonNullable<T>,
		B extends keyof Required<NonNullable<T>>[A],
		C extends keyof Required<Required<NonNullable<T>>[A]>[B]
	>(
		field: A,
		f2: B,
		f3: C,
	): ReactModel<Required<Required<NonNullable<T>>[A]>[B][C]>;
	/**
	 * * Returns a model of its member fields
	 * * Call "destroy()" function when it is no longer needed
	 */
	public getModel<
		A extends keyof NonNullable<T>,
		B extends keyof Required<NonNullable<T>>[A]
	>(
		field: A,
		f2: B
	): ReactModel<Required<NonNullable<T>>[A][B]>;
	/**
	 * * Returns a model of its member fields
	 * * Call "destroy()" function when it is no longer needed
	 */
	public getModel<A extends keyof NonNullable<T>>(field: A): ReactModel<NonNullable<T>[A]>;

	public getModel(arg1: any, arg2?: any, arg3?: any, arg4?: any): any {
		if (typeof arg1 === "object") {
			return this._getSubModel(arg1);
		} else if (arg1 === "+proxy") {
			return this._getSubModel_AddValueField("");
		} else return this._getSubModelOfField(arg1 as any, arg2, arg3, arg4, "");
	}


	/**
	 * Add a root field to the state.
	 *
	 * Example:
	 * `getModel_remap(null, 'value')`
	 * converts `state=5` to `state={value:5}`
	*/
	public getModel_remap<A extends KS<T>, P>(not_used:null, new_root_name: KS<P>)
		:
		ReactModel<{[X in keyof P]: T}>;

	/**
	 * Map field names, max 5 fields
	 * */
	public getModel_remap<F1 extends keyof NonNullable<T>, T1, F2 extends keyof NonNullable<T>, T2, F3 extends keyof NonNullable<T>, T3, F4 extends keyof NonNullable<T>, T4, F5 extends keyof NonNullable<T>, T5>(
		//field 1
		old_field_name:F1, new_field_name: KS<T1>,
		//field 2
		old_field_name2?:F2, new_field_name2?: KS<T2>,
		//field 3
		old_field_name3?:F3,new_field_name3?: KS<T3>,
		//field 4
		old_field_name4?:F4,new_field_name4?: KS<T4>,
		//field 5
		old_field_name5?:F5,new_field_name5?: KS<T5>
		)
			:
		ReactModel<
			{[X in F1 as keyof T1]: NonNullable<T>[X]}
			&
			{[X in F2 as keyof T2]: NonNullable<T>[X]}
			&
			{[X in F3 as keyof T3]: NonNullable<T>[X]}
			&
			{[X in F4 as keyof T4]: NonNullable<T>[X]}
			&
			{[X in F5 as keyof T5]: NonNullable<T>[X]}
		>;

	public getModel_remap(
		//field 1
		old_field_name:any, new_field_name: any,
		//field 2
		old_field_name2?:any, new_field_name2?: any,
		//field 3
		old_field_name3?:any,new_field_name3?: any,
		//field 4
		old_field_name4?:any,new_field_name4?: any,
		//field 5
		old_field_name5?:any,new_field_name5?: any
		)
			:
		any
	{
		return this.getModel({
			key: `${old_field_name}-${new_field_name}|${old_field_name2}-${new_field_name2}|${old_field_name3}-${new_field_name3}`,
			getter: (state:any)=>{
				//map root only
				if(! old_field_name)
					return {[new_field_name]: state};

				if(! state) state = {} as any;

				const ret = {[new_field_name]:state[old_field_name]} as any;
				//field 2
				if(new_field_name2 && old_field_name2){
					ret[new_field_name2] = state[old_field_name2] as any;
					//field 3
					if(new_field_name3 && old_field_name3){
						ret[new_field_name3] = state[old_field_name3] as any;
						//field 4
						if(new_field_name4 && old_field_name4){
							ret[new_field_name4] = state[old_field_name4] as any;
							//field 5
							if(new_field_name5 && old_field_name5){
								ret[new_field_name5] = state[old_field_name5] as any;
							}
						}
					}
				}
				return ret;
			},
			setter:(state:any, new_state)=>{
				if(new_state){
					//map root only
					if(! old_field_name)
						return new_state[new_field_name];

					//map other fields
					if(! state) state = {};

					state[old_field_name] = new_state[new_field_name]
					//field 2
					if(new_field_name2 && old_field_name2){
						state[old_field_name2] = new_state[new_field_name2]
						//field 3
						if(new_field_name3 && old_field_name3){
							state[old_field_name3] = new_state[new_field_name3]
							//field 4
							if(new_field_name4 && old_field_name4){
								state[old_field_name4] = new_state[new_field_name4]
								//field 5
								if(new_field_name5 && old_field_name5){
									state[old_field_name5] = new_state[new_field_name5]
								}
							}
						}
					}
					return state;
				}
			}
		})
	}


	/**
	 * Picks a list of fields. Similar to typescript Pick<T,K>
	 *
	 * @param fields
	 * @returns
	 */
	public getModel_pick<TKeys extends keyof NonNullable<T>>(fields: TKeys[]): ReactModel<Pick<NonNullable<T>, TKeys>>{
		const key = fields.join();
		return this.getModel({
			key,
			getter: (state:any)=>{
				if(! state) return state;
				const ret = {} as any;
				for(const k of fields){
					ret[k] = state[k];
				}
				return ret;
			},
			setter:(state:any, new_state)=>{
				if(new_state) {
					if(! state) state = {};
					for(const k of fields){
						state[k] = new_state[k];
					}
					return state;
				}
			}
		})
	}

	//-----------------------------------
	//---------- use sub model ----------
	//-----------------------------------

	private _useSubModel<K>(arg: TSubModel_NoKey<T, K>): TUseReturn<T> {
		const ret = this._getSubModel({
			key: 'sub.'+_useId(),
			getter: arg.getter,
			setter: arg.setter,
			allow_update: arg.allow_update
		});
		const _useEffect = useEffect;
		_useEffect(() => {
			return () => {
				ret.destroy();
			};
		}, []);

		return ret.useModel();
	}

	private _useSubModelOfField<K extends keyof T>(
		field_name: K,
		f2: any,
		f3: any,
		f4: any,
	): TUseReturn<T> {
		const ret = this._getSubModelOfField(field_name, f2, f3, f4, _useId());
		const _useEffect = useEffect;
		_useEffect(() => {
			return () => {
				ret.destroy();
			};
		}, []);
		return ret.useModel();
	}

	private _useSubModel_AddValueField(): TUseReturn<{ proxy: T }> {
		const ret = this._getSubModel_AddValueField(_useId());
		const _useEffect = useEffect;
		_useEffect(() => {
			return () => {
				ret.destroy();
			};
		}, []);
		return ret.useModel();
	}

	private _useCurrentModel(): TUseReturn<T> {
		this._render_count++;
		const _useState = useState;
		const [, setUpdateCounter] = _useState(0);

		const observer_name = "_useMdl_" + _useId();
		const _first_model = this._model;
		const _useEffect = useEffect;
		_useEffect(() => {
			//add observer
			this.addObserver(
				observer_name,
				() => {
					setUpdateCounter(++g_update_counter);
					// console.log(`<update request> model ${this.model_id}, ud=${g_update_counter}`);
				},
				this.model_id.toString(),
			);
			// setUpdateCounter(update_counter + 1);

			//before observer has been added, the model may have been changed
			//this is why _first_model is checked
			if (_first_model !== this._model) {
				setUpdateCounter(++g_update_counter);
				// console.log(`<update request 2> model ${this.model_id}, ud=${g_update_counter}`);
				// console.log('--- model change detected before addObserver ---');
			}

			return () => {
				//remove the observer
				this.removeObserver(observer_name);
				// console.log(`<unmount> model ${this.model_id}, ud=${g_update_counter}`);
			};
		}, [observer_name, setUpdateCounter, this]);
		return {
			state: this._model, //state, _model only freeze the first level object
			setState: this.setState.bind(this),
			model: this as any as ReactModel<any>
		}; // model_obj as Model<T>;
	}

	/**
	 * * A hook function that can only be called **in a component**.
	 * * The main difference from 'getModel()' is
	 * 	- the enclosing component will be rendered again if the model changes
	 * 	- the 'destroy()' function will be called automatically
	 * @param "+proxy" add a field "value" to hold the model value, so that the model can connect to HInput
	 */
	// public useModel(arg: "+proxy"): TUseReturn<{ proxy: T }>;
	public useModel<K>(arg: TSubModel_NoKey<T, K>): TUseReturn<K>;
	public useModel(): TUseReturn<T>;
	public useModel<
		A extends keyof NonNullable<T>,
		B extends keyof Required<NonNullable<T>>[A],
		C extends keyof Required<Required<NonNullable<T>>[A]>[B],
		D extends keyof Required<Required<Required<NonNullable<T>>[A]>[B]>[C],
	>(field: A, field2: B, field3: C, field4: D): TUseReturn<Required<Required<Required<NonNullable<T>>[A]>[B]>[C][D]>;
	public useModel<A extends keyof NonNullable<T>, B extends keyof Required<NonNullable<T>>[A], C extends keyof Required<Required<NonNullable<NonNullable<T>>>[A]>[B]>(
		field: A,
		field2: B,
		field3: C,
	): TUseReturn<Required<Required<NonNullable<T>>[A]>[B][C]>;
	public useModel<A extends keyof NonNullable<T>, B extends keyof Required<NonNullable<T>>[A]>(
		field: A,
		field2: B,
	): TUseReturn<Required<NonNullable<T>>[A][B]>;
	public useModel<A extends keyof NonNullable<T>>(field: A): TUseReturn<NonNullable<T>[A]>;

	public useModel(arg1?: any, arg2?: any, arg3?: any, arg4?: any): any {
		if (arg1 === undefined) {
			return this._useCurrentModel();
		} else if (typeof arg1 === "object") {
			return this._useSubModel(arg1);
		} else if (arg1 === "+proxy") {
			return this._useSubModel_AddValueField();
		} else {
			return this._useSubModelOfField(arg1, arg2, arg3, arg4);
		}
	}

	/**
	 * This is a shortcut to useModel({getter, setter}), with the "allow_update" set to true
	*/
	public useComputed<K>(getter:TSubModel<T, K>['getter'], setter:TSubModel<T, K>['setter']){
		return this.useModel({
			getter,
			setter,
			'allow_update':true,
		});
	}

	public static resetModelId() {
		model_id = 0;
	}
}

// type Partial<T> = {
//     [P in keyof T]?: T[P];
// };

//------------------------------------------------------------------------------
// With validator
//------------------------------------------------------------------------------
const key_error_count: KS<TValidatorCommon> =  '$err_count';

// type SkipParameter1<T extends (arg1:any,...args: any) => any> = T extends (arg1:any, ...args: infer P) => any ? P : never;
// type TFuncSkip1<T extends (arg1:any,...args: any) => any> = (...args:SkipParameter1<T>)=> ReturnType<T>;

// function increase(a:number, b:number){return a+b};
// const a:SkipParameter1<typeof increase> = null as any;


export class ReactModel<T, TCtrl extends {}={}> extends ReactModel_React<T> {

	constructor(options: TReactModelOptions<T, TCtrl>){
		super(options);
		if(options.controller)
			this.c = options.controller;
	}
	/** If the sub model does not sync to 'undefined', set this to true. This might cause UI issue */
	public static sync_undef_sub_model=false;
	//--------------------------------------------------------------------------------------
	// controller
	//--------------------------------------------------------------------------------------

	/**
	 * Controller functions that changes the model.
	 *
	 * Two ways to define the controller
	 * 1. In constructor, defined the `controller` option.
	 * 1. call addCtrl() to populate controller functions.
	 */
	public c: TCtrl;

	public get s(){
		return this._model;
	}

	//this implementation skips the first parameter
	// public addCtrl<TFuncName extends string, TFunc extends (model:ReactModel<T>,...args:any)=>void | Promise<void>>(name: TFuncName, func: TFunc):
	// 	ReactModel<T, TCtrl & Record<TFuncName, TFuncSkip1<TFunc>>>{
	// 	(this.ctrl as any)[name] = (...values:any)=>{
	// 		(func as any)(this, ...values);
	// 	};
	// 	return this as any;
	// }

	/**
	 * Add a controller function to the "ctrl" field of the model
	 *
	 * @param name name of the controller function
	 * @param func function body
	 * @returns a new model with "ctrl" populated with this function
	 */
	public addCtrl<TFuncName extends string, TFunc extends (...args:any)=>any | Promise<any>>(name: TFuncName, func: TFunc): ReactModel<T, TCtrl & Record<TFuncName, TFunc>>{
		if(! this.c) this.c = {} as any;
		(this.c as any)[name] = func;
		return this as any;
	}

	//--------------------------------------------------------------------------------------
	// validator
	//--------------------------------------------------------------------------------------

	private _validator: TReactModelValidator<T> | undefined;
	private _validator_enabled?: { [P: string | number | symbol]: boolean };

	private getObserverKey(field_name: "*" | keyof T) {
		return "RM438_validator-" + field_name.toString();
	}

	/**
	 * This is to remove the dependency on df-validator.
	 * To use this feature, assign df-validator/validateOneField to this field in index.tsx.
	 *
	 * Refer to the unit test for sample usage
	 */
	static validateOneField: typeof validateOneField;

	private _setValidator<A extends keyof T>(
		field_name: A,
		validator: TValueValidator,
		enabled: boolean,
	): void;
	private _setValidator<A extends keyof T>(
		field_name: A,
		func: (state: T, field_value: T[A]) => (string | void) | Promise<(string | void)>,
		enabled: boolean,
	): void;
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	private _setValidator<A extends keyof T>(
		field_name: "*",
		func: (state: T) => (string | void) | Promise<(string | void)>,
		enabled: boolean,
	): void;
	private _setValidator<A extends keyof T>(
		field_name: A | "*",
		func: any,
		enabled: boolean,
	): void {
		//do not add again. Otherwise, whether to run for the first is hard to handle, and the "enabled" status may also be changed unexpectedly
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		if (this._validator_enabled![field_name] !== undefined) {
			// console.error('validator exists: ' + field_name.toString() + ', key=' + this.key);
			return;
		}

		//--------------------------------------------------------------------------------------
		// Handle TValueValidator
		//--------------------------------------------------------------------------------------

		if (typeof func === "object") {
			this._setValidator(
				field_name as A,
				async (state, value) => {
					// const validateOneField = (await import("df-validator")).validateOneField;
					const f = ReactModel.validateOneField;
					if(! f) throw Error("(RM705)ReactModel.validateOneField not assigned.")

					const error = await f(field_name as string, value, func);
					//map: do not show variable name
					if (error.length)
						return error
							.map(x => x.replace((field_name as string) + ":", ""))
							.join("\n");
				},
				enabled,
			);
			return;
		}

		//--------------------------------------------------------------------------------------
		// Handle function
		//--------------------------------------------------------------------------------------
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		this._validator_enabled![field_name] = enabled;
		const field_name_str = field_name.toString();

		//the function to update the validation message
		//it will be called by async and sync function
		const _updateValidationMsg = (msg:string)=>{
			{
				this._validator?.setState(state => {
					if (msg) (state as any)[field_name] = msg;
					else delete state[field_name];
					state.$err_count = Object.keys(state).length - 1;
				});
			}
			//save error msg to root validator
			{
				this._updateParentValidator();
			}
			this._busyValidators?.remove(field_name_str);
		}
		let update_counter = 0;

		this.addObserver(
			this.getObserverKey(field_name),
			state => {
				update_counter++;
				this._busyValidators?.add(field_name_str);
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				const en = this._validator_enabled![field_name];
				//call validation function
				if (en) {
					if (field_name !== "*") {
						// eslint-disable-next-line no-var
						var msg = func(state, state?.[field_name]);
						if(msg instanceof Promise){
							const local_update_counter = update_counter;//make sure only the latest message is used
							msg.then(error_msg=>{
								if(local_update_counter === update_counter)//if the error message is stale, then discard it.
									_updateValidationMsg(error_msg);
							}).catch((ex)=>{
								if(local_update_counter === update_counter)//if the error message is stale, then discard it.
									_updateValidationMsg("Error: "+ex.message || ex);
							})
							return;
						}
					} else {
						msg = func(state);
					}
				} else {
					msg = "";
				}
				//save error msg to local validator
				_updateValidationMsg(msg);
			},
			this._validator?.model_id.toString(),
			enabled, //run validator now
		);
	}

	private _busyValidators: BusyWorker | undefined;

	private _updateParentValidator() {
		let curr_model: ReactModel<any,any> = this;
		while(curr_model?._parent_model){
			const parent = curr_model._parent_model;
			const parent_validator = parent.validator(true);
			const curr_errors = curr_model.validator(true).getState();
			parent_validator.setState(state=>{
				//delete old error key from parent model
				{
					const error_keys = Object.keys(state);
					const curr_key = curr_model.key+'->';
					for(const error_key of error_keys){
						if(error_key?.startsWith(curr_key)){
							delete state[error_key];
						}
					}
				}
				//add new error key to parent model
				{
					const error_keys = Object.keys(curr_errors);
					for(const error_key of error_keys){
						if(error_key === key_error_count) continue;
						const new_error_key = `${curr_model.key}->${error_key}`;//`${curr_model.key}[${error_key}]`;
						state[new_error_key] = (curr_errors as any)[error_key];
					}
				}
				//update error count
				state.$err_count = Object.keys(state).length-1;
			});
			curr_model = parent;
		}

		// this._parent_model?.validator(true).setState((state: any) => {
		// 	//'->' sync edit ref_urv452
		// 	const key = this.key + "->" + field_name_str;
		// 	if (msg) state[key] = msg;
		// 	else delete state[key];
		// 	state.$err_count = Object.keys(state).length - 1;
		// });
	}

	private _enableAllValidators(enabled: boolean) {
		//current level
		for (const p in this._validator_enabled) {
			this._enableValidator(p as any, enabled);
		}

		//all children
		Object.values(this._partial_models || {}).forEach(x => {
			x._enableAllValidators(enabled);
		});
	}

	/** enable its own validator and all children validators */
	private async _validate():Promise<boolean> {
		this._enableAllValidators(true);
		// Object.values(this._partial_models || {}).forEach((x) => {
		// 	x.validator(false)?.validate();
		// });
		await this._busyValidators?.waitFinish();
		return !this.validator(false)?.getState().$err_count;
	}

	/**
	 * Use case
	 * 1. the validators are not enable on page load
	 * 2. When the user changes one field, one validator is enabled
	 * 3. When the user clicks the "submit" button, call enableAllValidators()
	 */
	private _enableValidator(field_name: "*" | keyof T, enabled: boolean): void {
		if (!this._validator_enabled) return;
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		const en = this._validator_enabled![field_name];
		if (en === undefined) {
			return; //console.error('Invalid field_name: ' + field_name.toString());
		}
		if (en !== enabled && en !== undefined) {
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			this._validator_enabled![field_name] = enabled;
			if (this._observers) {
				this._observers[this.getObserverKey(field_name)].onModelChange(this.getState());
			}
		}
	}

	private _isValidatorEnabled(field_name: "*" | keyof T) {
		if (!this._validator_enabled) return false;
		return !!this._validator_enabled[field_name];
	}

	public validator(allow_create?: boolean): TReactModelValidator<T> | undefined;
	public validator(allow_create: true): TReactModelValidator<T>;
	public validator(allow_create: boolean): TReactModelValidator<T> | undefined {
		if (allow_create && !this._validator) {
			this._initValidator();
			if(! this._busyValidators){
				this._busyValidators = new BusyWorker();
			}
		}
		//: undefined | Omit<typeof this._validator, 'enableValidator' | 'getRootModel' | 'setState'> {
		return this._validator;
	}

	private _root_model?: ReactModel<any> | null;

	public getRootModel(): ReactModel<any> | null {
		if (this._root_model === undefined) {
			let p = this._parent_model;
			while (p?._parent_model) {
				p = p._parent_model;
			}
			this._root_model = p || null;
		}
		return this._root_model;
	}

	public getDebugInfo() {
		const ss = [] as string[];
		ss.push("Model Id: " + this.model_id);
		ss.push("Validator Id: " + (this._validator?.model_id || "(none)"));
		ss.push("Observers: " + this.observers().join(","));
		ss.push(`Parent Id: ${this._parent_model?.model_id || "(none)"}`);
		const m = Object.values(this._partial_models);
		const sub = m.map(x => x.model_id).join(",");
		ss.push(`Sub model Ids: ${sub}`);
		return ss;
	}

	public destroy(): void {
		super.destroy();
		this._validator?.destroy();
		//remove the validator msg from root model
		this.getRootModel()?._validator?.setState(state => {
			for (const p in state) {
				//'->' sync edit ref_urv452
				if (p.startsWith(this.key + "->")) {
					delete state[p];
				}
			}
			state.$err_count = Object.keys(state).length - 1;
		});
	}

	private _initValidator() {
		if (!this._validator) {
			this._validator = new TReactModelValidator<T>({ model: { $err_count: 0 as any } });
			this._validator_enabled = {};
			const v = this._validator;

			v.add = this._setValidator.bind(this);
			v.enable = this._enableValidator.bind(this);
			v.validate = this._validate.bind(this);
			v.isEnabled = this._isValidatorEnabled.bind(this);
			v.enableAll = this._enableAllValidators.bind(this);

			//create validator for the root model
			this.getRootModel()?._initValidator();
		}
	}
}

type TValidatorCommon = { $err_count: number; "*"?: string };

class TReactModelValidator<T> extends ReactModel<
	{ [P in keyof T]?: string } & TValidatorCommon
> {
	/** Add validator to a field. use '*' for all fields. Raise an error if the validator exists */
	public add<A extends keyof T>(
		field_name: A,
		validator: TValueValidator,
		enabled: boolean,
	): void;
	public add<A extends keyof T>(
		field_name: A,
		func: (state: T, field_value: T[A]) => string | void | Promise<string | void>,
		enabled: boolean,
	): void;
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public add<A extends keyof T>(
		field_name: "*",
		func: (state: T) => string | void | Promise<string | void>,
		enabled: boolean,
	): void;
	// eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
	public add<A extends keyof T>(field_name: A | "*", func: any, enabled: boolean): void {}

	/** Enable all validator and all its sub model validators */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
	public enableAll(enabled: boolean): void {}
	/** call enableAll(true), and then return true if validation pass */
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	public async validate(): Promise<boolean> {return true;}
	/** enable validation of an individual field */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
	public enable(field_name: "*" | keyof T, enabled: boolean): void {}
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public isEnabled(field_name: "*" | keyof T): boolean {
		return false;
	}
}

// export class ReactModelCtrl<T, TCtrl extends {}> extends ReactModel<T>{
// 	public ctrl: TCtrl = {} as any;

// 	public addCtrl<TFuncName extends string, TFunc extends (model:ReactModel<T>)=>void | Promise<void>>(name: TFuncName, func: TFunc): ReactModelCtrl<T, TCtrl & Record<TFuncName, TFunc>>{
// 		(this.ctrl as any)[name] = func;
// 		return this as any;
// 	}
// }

// class TestType<T extends {}>{
// 	public ctrl:T;
// 	public add<Token extends string, TFunc>(name:Token, func:TFunc): TestType<T & Record<Token, TFunc>>{
// 		return this as any;
// 	}
// }

// const test = new TestType();

// test.add('good', (model:number)=>{

// }).ctrl.good(2)

// const model_test = new ReactModel(
// 	{
// 		model:{a:1,b:2},
// 		controller: {
// 			load:(value:string)=>{
// 				return true;
// 			},
// 			save:(value:string)=>{
// 				return true;
// 			}
// 		}
// });
// model_test.c.load("21");




export type TReactModelData<T> = T extends ReactModel<infer TData> ? TData : any;
export type TReactModelCtrl<T> = T extends ReactModel<infer TData,infer TCtrl> ? TCtrl : any;

